import { type FormEvent, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Alert, Badge, Button, Select, Table } from '@/design-system';
import { ProblemAlert } from '@/app/ProblemAlert';
import { ApiError } from '@/lib/api';
import type { ApplicationStatus } from '@/lib/intake/api';
import { kilobytes } from '@/lib/intake/documents';
import { formatDate, formatDateTime } from '@/lib/format';
import { type Problem, toProblem } from '@/lib/problem';
import {
  type Detail,
  type FileItem,
  type Outcome,
  OUTCOME_LABELS,
  review,
  STATUS_LABELS,
  statusLabel,
  statusTone,
} from './api';
import styles from './review.module.css';

const SCAN_LABELS: Record<FileItem['scanStatus'], string> = {
  PENDING: 'Being checked for malware',
  CLEAN: 'Scanned clean',
  INFECTED: 'Malware found',
  FAILED: 'Could not be checked',
};

/** How a trail entry reads. Its detail holds ids and counts only (constraint 4). */
const ACTIONS: Record<string, string> = {
  APPLICATION_CREATED: 'Started the application',
  CONSENT_GIVEN: 'Confirmed they may share third parties’ data',
  FILE_ADDED: 'Attached a file',
  FILE_REFUSED: 'A file was refused',
  FILE_REMOVED: 'Removed a file',
  FILE_SCANNED: 'A file was scanned',
  SUMMARY_EXPORTED: 'Downloaded or copied the summary',
  APPLICATION_SUBMITTED: 'Sent the application',
  APPLICATION_VIEWED: 'Opened the application',
  FILE_DOWNLOADED: 'Downloaded a file',
  STATUS_CHANGED: 'Changed the status',
};

/**
 * One application, as the advisory team reads it: the answers with every gap
 * flagged, the documents, and the way to move it on. Opening this page is
 * recorded, as is every download.
 */
export function ApplicationPage() {
  const { applicationId = '' } = useParams();
  const [detail, setDetail] = useState<Detail | null>(null);
  const [problem, setProblem] = useState<Problem | null>(null);

  useEffect(() => {
    let cancelled = false;
    review
      .detail(applicationId)
      .then((d) => {
        if (!cancelled) setDetail(d);
      })
      .catch((error: unknown) => {
        if (!cancelled) setProblem(toProblem(error));
      });
    return () => {
      cancelled = true;
    };
  }, [applicationId]);

  if (problem && !detail) {
    return (
      <div className={styles.page}>
        <Link to="/applications">Back to applications</Link>
        <ProblemAlert problem={problem} title="This application could not be opened" />
      </div>
    );
  }
  if (!detail) return <p className={styles.muted}>Loading the application.</p>;

  const gaps = detail.sections.filter((s) => !s.complete);

  return (
    <div className={styles.page}>
      <div className={styles.intro}>
        <Link to="/applications">Back to applications</Link>
        <h1 className={styles.title}>{detail.reference}</h1>
        <div className={styles.headline}>
          <Badge tone={statusTone(detail.status)}>{statusLabel(detail.status, detail.outcome)}</Badge>
          <span>{detail.applicantEmail}</span>
          <span className={styles.muted}>Sent {formatDate(detail.submittedAt)}</span>
        </div>
      </div>

      {detail.purgedAt && (
        <Alert tone="info" title="Answers and files deleted">
          Deleted on {formatDate(detail.purgedAt)} under the retention period. The reference, status history and
          trail are kept.
        </Alert>
      )}

      <StatusChange detail={detail} onMoved={setDetail} />

      <section aria-labelledby="answers" className={styles.block}>
        <h2 id="answers" className={styles.sectionTitle}>
          Answers
        </h2>
        <Alert tone={gaps.length === 0 ? 'success' : 'info'}>
          {detail.banner}
          {gaps.length > 0 && <> Incomplete: {gaps.map((s) => `${s.number}. ${s.title}`).join('; ')}.</>}
        </Alert>
        {detail.sections.map((section) => (
          <section key={section.number} aria-labelledby={`section-${section.number}`} className={styles.section}>
            <div className={styles.sectionHead}>
              <h3 id={`section-${section.number}`} className={styles.subtitle}>
                {section.number}. {section.title}
              </h3>
              {!section.complete && <Badge tone="danger">Incomplete</Badge>}
            </div>
            <dl className={styles.answers}>
              {section.lines.map((line) => (
                <div key={line.question} className={styles.answer}>
                  <dt>{line.question}</dt>
                  <dd
                    className={
                      line.kind === 'NOT_ANSWERED' ? styles.gap : line.kind === 'SKIPPED' ? styles.skipped : undefined
                    }
                  >
                    {line.answer}
                  </dd>
                </div>
              ))}
            </dl>
          </section>
        ))}
      </section>

      <Documents detail={detail} />

      <section aria-labelledby="history" className={styles.block}>
        <h2 id="history" className={styles.sectionTitle}>
          Status history
        </h2>
        <Table
          caption="Every change of status, with its reason"
          columns={[
            { key: 'when', header: 'When', nowrap: true },
            { key: 'change', header: 'Change' },
            { key: 'reason', header: 'Reason' },
            { key: 'by', header: 'By' },
          ]}
          rows={detail.history.map((h, i) => ({
            key: String(i),
            cells: {
              when: formatDateTime(h.at),
              change: `${STATUS_LABELS[h.from]} to ${STATUS_LABELS[h.to]}`,
              reason: h.reason ?? '',
              by: h.by,
            },
          }))}
        />
      </section>

      <section aria-labelledby="trail" className={styles.block}>
        <h2 id="trail" className={styles.sectionTitle}>
          Trail
        </h2>
        <p className={styles.muted}>Everything recorded about this application, including each time it was opened.</p>
        <Table
          caption="The application’s trail, oldest first"
          columns={[
            { key: 'when', header: 'When', nowrap: true },
            { key: 'what', header: 'What' },
            { key: 'by', header: 'By' },
            { key: 'detail', header: 'Detail' },
          ]}
          rows={detail.trail.map((t, i) => ({
            key: String(i),
            cells: {
              when: formatDateTime(t.at),
              what: ACTIONS[t.action] ?? t.action,
              by: t.by,
              detail: <span className={styles.detail}>{t.detail}</span>,
            },
          }))}
        />
      </section>
    </div>
  );
}

function Documents({ detail }: { detail: Detail }) {
  const [problem, setProblem] = useState<Problem | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  async function download(file: FileItem) {
    setBusy(file.fileId);
    setProblem(null);
    try {
      const link = await review.downloadLink(detail.applicationId, file.fileId);
      // The link downloads the file under its own name; the page stays where it is.
      window.location.assign(link.url);
    } catch (error) {
      setProblem(toProblem(error));
    } finally {
      setBusy(null);
    }
  }

  return (
    <section aria-labelledby="documents" className={styles.block}>
      <h2 id="documents" className={styles.sectionTitle}>
        Documents
      </h2>
      <p className={styles.muted}>Each download link works for five minutes, and each is recorded in the trail.</p>
      {problem && <ProblemAlert problem={problem} title="The file could not be downloaded" />}
      <ul className={styles.requirements}>
        {detail.documents.map((requirement) => (
          <li key={requirement.id} className={styles.requirement}>
            <div className={styles.sectionHead}>
              <span className={styles.requirementTitle}>{requirement.title}</span>
              {requirement.highlighted && <Badge tone="accent">Relevant to the answers</Badge>}
            </div>
            {requirement.files.length === 0 ? (
              <span className={styles.skipped}>Not provided</span>
            ) : (
              <ul className={styles.files}>
                {requirement.files.map((file) => (
                  <li key={file.fileId} className={styles.file}>
                    <span className={styles.fileName}>{file.fileName}</span>
                    <span className={styles.muted}>{kilobytes(file.sizeBytes)}</span>
                    {file.downloadable ? (
                      <Button
                        variant="secondary"
                        size="sm"
                        disabled={busy === file.fileId}
                        aria-label={`Download ${file.fileName}`}
                        onClick={() => void download(file)}
                      >
                        Download
                      </Button>
                    ) : (
                      <Badge tone={file.scanStatus === 'INFECTED' ? 'danger' : 'neutral'}>
                        {SCAN_LABELS[file.scanStatus]}
                      </Badge>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}

const REASON_MAX = 2000;

function StatusChange({ detail, onMoved }: { detail: Detail; onMoved: (d: Detail) => void }) {
  const [to, setTo] = useState<ApplicationStatus | ''>('');
  const [outcome, setOutcome] = useState<Outcome | ''>('');
  const [reason, setReason] = useState('');
  const [problem, setProblem] = useState<Problem | null>(null);
  const [busy, setBusy] = useState(false);

  if (detail.next.length === 0) {
    return (
      <p className={styles.muted}>
        {detail.status === 'INFORMATION_REQUESTED'
          ? 'Waiting for the applicant to add what was asked for and send it again.'
          : 'Closed. Its status no longer changes.'}
      </p>
    );
  }

  async function move(event: FormEvent) {
    event.preventDefault();
    if (to === '') return;
    setBusy(true);
    setProblem(null);
    try {
      onMoved(await review.move(detail.applicationId, detail.status, to, reason, outcome === '' ? null : outcome));
      setTo('');
      setOutcome('');
      setReason('');
    } catch (error) {
      // Somebody moved it first: show it as it now is, and say so.
      if (error instanceof ApiError && error.code === 'status_changed' && error.body?.latest) {
        onMoved(error.body.latest as Detail);
      }
      setProblem(toProblem(error));
    } finally {
      setBusy(false);
    }
  }

  const reasonId = 'status-reason';
  const tellsApplicant = to === 'INFORMATION_REQUESTED';

  return (
    <form className={styles.panel} onSubmit={(e) => void move(e)} aria-labelledby="move-title">
      <h2 id="move-title" className={styles.panelTitle}>
        Change the status
      </h2>
      <Select
        label="Move to"
        value={to}
        options={[
          { value: '', label: 'Choose a status' },
          ...detail.next.map((s) => ({ value: s, label: STATUS_LABELS[s] })),
        ]}
        onChange={(e) => setTo(e.target.value as ApplicationStatus | '')}
      />
      {to === 'CLOSED' && (
        <Select
          label="Outcome"
          value={outcome}
          options={[
            { value: '', label: 'Choose an outcome' },
            { value: 'ACCEPTED', label: OUTCOME_LABELS.ACCEPTED },
            { value: 'DECLINED', label: OUTCOME_LABELS.DECLINED },
          ]}
          onChange={(e) => setOutcome(e.target.value as Outcome | '')}
        />
      )}
      <div className={styles.field}>
        <label htmlFor={reasonId} className={styles.label}>
          Reason
        </label>
        <textarea
          id={reasonId}
          className={styles.textarea}
          value={reason}
          maxLength={REASON_MAX}
          rows={4}
          required
          aria-describedby={`${reasonId}-help`}
          onChange={(e) => setReason(e.target.value)}
        />
        <span id={`${reasonId}-help`} className={styles.muted}>
          {tellsApplicant
            ? 'Emailed to the applicant and shown to them when they sign in. Write it for them, and do not repeat their answers or file names.'
            : 'Kept in the status history.'}
        </span>
      </div>
      {problem && <ProblemAlert problem={problem} title="The status was not changed" />}
      <div className={styles.buttons}>
        <Button
          type="submit"
          disabled={busy || to === '' || reason.trim() === '' || (to === 'CLOSED' && outcome === '')}
        >
          {tellsApplicant ? 'Ask the applicant' : 'Change status'}
        </Button>
      </div>
    </form>
  );
}
